import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaGraduationCap, FaTasks, FaChartLine, FaBookOpen, FaFileUpload, FaClipboardList, FaCheck, FaTimes } from 'react-icons/fa';

const featureSlides = [
  {
    title: 'Smart Organization',
    icon: FaTasks,
    summary: 'Keep courses, assignments, and exams in one intuitive dashboard.',
    details: 'Everything stays connected so deadlines, course work, and weekly priorities are easy to track from one place.',
    points: ['Centralized course planning', 'Fast assignment visibility', 'Built for busy semesters']
  },
  {
    title: 'Progress Tracking',
    icon: FaChartLine,
    summary: 'Visualize your academic growth with analytics and GPA tracking.',
    details: 'Use progress insights to stay on top of goals, spot weak points early, and make better study decisions.',
    points: ['GPA visibility', 'Progress trends', 'Actionable insights']
  },
  {
    title: 'Flashcards & Study Tools',
    icon: FaBookOpen,
    summary: 'Build flashcards and keep study sessions tied to your coursework.',
    details: 'Flashcards are integrated into the workflow so you can review material without jumping between tools.',
    points: ['Study-ready flashcards', 'Course-linked review', 'Import-friendly workflows']
  },
  {
    title: 'File Uploads',
    icon: FaFileUpload,
    summary: 'Store notes and supporting documents with your class materials.',
    details: 'Upload reference files alongside your courses so the documents you need are always close to the work they support.',
    points: ['Notes and documents in one place', 'Course-linked uploads', 'Easy access on mobile']
  },
  {
    title: 'Study Tracking',
    icon: FaClipboardList,
    summary: 'Track study progress and review sessions with built-in visibility.',
    details: 'See what you studied, what still needs attention, and how your workload is moving week by week.',
    points: ['Review session visibility', 'Study momentum tracking', 'Focus on what is next']
  },
  {
    title: 'Academic Success',
    icon: FaGraduationCap,
    summary: 'Tools designed specifically for students to reduce stress and improve results.',
    details: 'Plan confidently with a workflow that supports school responsibilities without adding extra friction.',
    points: ['Lower planning friction', 'Student-first design', 'Less missed work']
  }
];



const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: ${props => props.theme.background};
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.primary} 0%, ${props => props.theme.buttonPrimaryHover || '#4752c4'} 100%);
  color: white;
  padding: 6rem 2rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  color: white;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 600px;
  margin: 0 auto;
  opacity: 0.9;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
`;

const ContentSection = styled.section`
  padding: 0 2rem 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const MissionCard = styled.div`
  background: ${props => props.theme.cardBackground};
  border-radius: 16px;
  padding: 3rem;
  margin-bottom: 4rem;
  box-shadow: 0 4px 20px ${props => props.theme.shadow || 'rgba(0, 0, 0, 0.05)'};
  text-align: center;
  border: 1px solid ${props => props.theme.border};
  margin-top: -3rem; /* Overlap with hero */
  position: relative;
  z-index: 10;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.textPrimary};
  font-weight: 600;
`;

const Text = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  &:last-child {
    margin-bottom: 0;
  }
`;


const FeatureCarousel = styled.div`
  margin-bottom: 4rem;
`;

const CarouselFrame = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 20px;
  box-shadow: 0 4px 20px ${props => props.theme.shadow || 'rgba(0, 0, 0, 0.05)'};
  overflow: hidden;
`;

const CarouselToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1rem 0;
  position: relative;

  @media (max-width: 640px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const CarouselControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ControlButton = styled.button`
  border: none;
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.textPrimary};
  border-radius: 12px;
  padding: 0.6rem 0.85rem;
  min-width: 40px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(16,24,40,0.06);
  transition: transform 0.18s ease, background 0.18s ease, color 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.theme.primary};
    color: white;
  }
`;

const PauseButton = styled(ControlButton)`
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
  font-weight: 700;
  border-radius: 10px;
`;

const SlideCounter = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.95rem;
  font-weight: 600;
  white-space: nowrap;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 3;
`;

const EnlargeButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 0.6rem 1rem;
  min-height: 40px;
  background: linear-gradient(90deg, ${props => props.theme.primary}, ${props => props.theme.buttonPrimaryHover || '#3b4ed6'});
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 30px rgba(59,80,212,0.12);
  transition: transform 0.18s ease, opacity 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    opacity: 0.98;
  }
`;

const CarouselViewport = styled.div`
  overflow: hidden;
  touch-action: pan-y;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const CarouselTrack = styled.div`
  display: flex;
  transform: translateX(-${props => props.$currentIndex * 100}%);
  transition: transform 0.45s ease;
`;

const CarouselSlide = styled.article`
  min-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  box-sizing: border-box;
`;

const SlideCard = styled.div`
  width: min(92%, 820px);
  aspect-ratio: 4 / 3;
  border-radius: 18px;
  background: linear-gradient(180deg, ${props => `${props.theme.primary}10`} 0%, ${props => props.theme.cardBackground} 100%);
  border: 1px solid ${props => props.theme.border};
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 1rem;

  @media (max-width: 640px) {
    width: calc(100vw - 48px);
    aspect-ratio: 4 / 3;
    padding: 1.2rem;
  }
`;

const SlideIcon = styled.div`
  height: 84px;
  width: 84px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => `${props.theme.primary}15`};
  color: ${props => props.theme.primary};
  font-size: 2.5rem;
`;

const SlideTitle = styled.h3`
  font-size: 1.5rem;
  color: ${props => props.theme.textPrimary};
  margin: 0;
`;

const SlideSummary = styled.p`
  font-size: 1.05rem;
  color: ${props => props.theme.textSecondary};
  line-height: 1.7;
  margin: 0 auto;
  max-width: 700px;
`;

const SlidePoints = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  padding: 0;
  margin: 0;
`;

const SlidePoint = styled.li`
  background: ${props => `${props.theme.primary}10`};
  color: ${props => props.theme.textPrimary};
  border: 1px solid ${props => props.theme.border};
  border-radius: 999px;
  padding: 0.55rem 0.9rem;
  font-size: 0.92rem;
  font-weight: 600;
`;


const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 0 1rem 1rem;
`;

const Dot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${props => props.$active ? props.theme.primary : props.theme.border};
  transform: ${props => props.$active ? 'scale(1.2)' : 'scale(1)'};
  transition: transform 0.2s ease, background 0.2s ease;
`;

const SlideModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const SlideModalCard = styled.div`
  width: min(100%, 720px);
  max-height: min(90vh, 780px);
  overflow: auto;
  border-radius: 24px;
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  padding: 2rem;
  position: relative;
`;

const SlideModalClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  border: none;
  border-radius: 999px;
  width: 44px;
  height: 44px;
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
`;

const ModalIcon = styled(SlideIcon)`
  margin: 0 auto;
`;

const ModalTitle = styled.h3`
  margin: 1rem 0 0.75rem;
  font-size: 1.75rem;
  text-align: center;
  color: ${props => props.theme.textPrimary};
`;

const ModalSummary = styled.p`
  color: ${props => props.theme.textSecondary};
  line-height: 1.8;
  text-align: center;
  margin: 0 auto 1.25rem;
  max-width: 620px;
`;

const ModalPoints = styled(SlidePoints)`
  margin-bottom: 1.5rem;
`;

const ModalDetails = styled.p`
  color: ${props => props.theme.textPrimary};
  line-height: 1.8;
  text-align: left;
  margin: 0;
`;





const CTASection = styled.section`
  text-align: center;
  padding: 4rem 2rem;
  background: ${props => props.theme.backgroundSecondary};
  border-radius: 16px;
  margin-bottom: 2rem;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: ${props => props.theme.primary};
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px ${props => props.theme.shadow || 'rgba(0, 0, 0, 0.1)'};

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${props => props.theme.shadowMedium || 'rgba(0, 0, 0, 0.15)'};
    color: white;
  }
`;

const ComparisonSection = styled.section`
  padding: 0 2rem 4rem 2rem;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
`;

const ComparisonTable = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px ${props => props.theme.shadow || 'rgba(0, 0, 0, 0.05)'};
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.cardBackground};

  @media (max-width: 640px) {
    grid-template-columns: minmax(9rem, 1.4fr) repeat(2, minmax(4.5rem, 0.8fr));
  }
`;

const TableRow = styled.div`
  display: contents;
  
  &:last-child div {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  padding: 1.5rem;
  background: ${props => props.$highlight ? `${props.theme.primary}08` : 'transparent'};
  border-bottom: 1px solid ${props => props.theme.border};
  border-right: 1px solid ${props => props.theme.border};
  display: flex;
  align-items: center;
  justify-content: ${props => props.$center ? 'center' : 'flex-start'};
  color: ${props => props.theme.textPrimary};
  font-weight: ${props => props.$header ? '700' : '500'};
  font-size: ${props => props.$header ? '1.1rem' : '1rem'};
  min-width: 0;
  word-break: break-word;

  &:nth-child(3n) {
    border-right: none;
  }

  svg {
    font-size: 1.25rem;
  }

  @media (max-width: 640px) {
    padding: 1rem 0.75rem;
    font-size: ${props => props.$header ? '0.9rem' : '0.82rem'};

    svg {
      font-size: 1rem;
    }
  }
`;

const CheckMark = styled(FaCheck)`
  color: ${props => props.theme.buttonSuccess || '#28a745'};
`;

const CrossMark = styled(FaTimes)`
  color: ${props => props.theme.buttonDanger || '#dc3545'};
  opacity: 0.5;
`;

const About = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (isPaused || isModalOpen) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setCurrentSlide(previousSlide => (previousSlide + 1) % featureSlides.length);
    }, 4500);

    return () => window.clearInterval(timer);
  }, [isPaused, isModalOpen]);

  const goToSlide = (index) => {
    setCurrentSlide((index + featureSlides.length) % featureSlides.length);
  };

  const nextSlide = () => {
    setCurrentSlide(previousSlide => (previousSlide + 1) % featureSlides.length);
  };

  const previousSlide = () => {
    setCurrentSlide(previousSlide => (previousSlide - 1 + featureSlides.length) % featureSlides.length);
  };

  const handleTouchStart = (event) => {
    setTouchStartX(event.touches[0].clientX);
    setTouchEndX(null);
  };

  const handleTouchMove = (event) => {
    setTouchEndX(event.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) {
      return;
    }

    const swipeDistance = touchStartX - touchEndX;

    if (Math.abs(swipeDistance) > 50) {
      if (swipeDistance > 0) {
        nextSlide();
      } else {
        previousSlide();
      }
    }

    setTouchStartX(null);
    setTouchEndX(null);
  };

  const openModal = () => {
    setIsModalOpen(true);
    setIsPaused(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const activeSlide = featureSlides[currentSlide];

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Turn Stress Into Success</HeroTitle>
        <HeroSubtitle>
          Your all-in-one academic companion designed to help you master your journey from high school to university and beyond.
        </HeroSubtitle>
      </HeroSection>

      <ContentSection>
        <MissionCard>
          <SectionTitle>Our Mission</SectionTitle>
          <Text>
            We understand the challenges of managing multiple courses, assignments, and deadlines while trying to maintain a healthy work-life balance.
          </Text>
          <Text>
            Our mission is to provide intelligent planning tools that empower students to master their academic journey. Whether you're preparing for college or tackling complex university subjects, PlanMySemester adapts to your needs.
          </Text>
        </MissionCard>

        <SectionTitle style={{ textAlign: 'center', marginBottom: '3rem' }}>Why Choose Us?</SectionTitle>

        <FeatureCarousel ref={carouselRef}>
          <CarouselFrame>
            <CarouselToolbar>
              <CarouselControls>
                <ControlButton onClick={previousSlide} aria-label="Previous feature">
                  ‹
                </ControlButton>
                <PauseButton onClick={() => setIsPaused(previous => !previous)} aria-pressed={isPaused}>
                  {isPaused ? 'Resume' : 'Pause'}
                </PauseButton>
                <ControlButton onClick={nextSlide} aria-label="Next feature">
                  ›
                </ControlButton>
              </CarouselControls>
              <SlideCounter>
                {currentSlide + 1} / {featureSlides.length}
              </SlideCounter>
              <EnlargeButton onClick={openModal}>Focused View</EnlargeButton>
            </CarouselToolbar>

            <CarouselViewport
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <CarouselTrack $currentIndex={currentSlide}>
                {featureSlides.map((slide) => {
                  const SlideIconComponent = slide.icon;

                  return (
                    <CarouselSlide key={slide.title}>
                      <SlideCard>
                        <SlideIcon>
                          <SlideIconComponent />
                        </SlideIcon>
                        <SlideTitle>{slide.title}</SlideTitle>
                        <SlideSummary>{slide.summary}</SlideSummary>
                        <SlidePoints>
                          {slide.points.map((point) => (
                            <SlidePoint key={point}>{point}</SlidePoint>
                          ))}
                        </SlidePoints>
                        {/* Removed inline Prev/Next on slide cards per request */}
                      </SlideCard>
                    </CarouselSlide>
                  );
                })}
              </CarouselTrack>
            </CarouselViewport>

            <Dots>
              {featureSlides.map((slide, index) => (
                <Dot
                  key={slide.title}
                  $active={index === currentSlide}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to ${slide.title}`}
                />
              ))}
            </Dots>
          </CarouselFrame>
        </FeatureCarousel>

        <ComparisonSection>
          <SectionTitle style={{ textAlign: 'center', marginBottom: '3rem' }}>Compare the Difference</SectionTitle>
          <ComparisonTable>
            <TableRow>
              <TableCell $header>Feature</TableCell>
              <TableCell $header $center $highlight>PlanMySemester</TableCell>
              <TableCell $header $center>Traditional Planners</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Integrated Flashcards</TableCell>
              <TableCell $center $highlight><CheckMark /></TableCell>
              <TableCell $center><CrossMark /></TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Course-scoped File Uploads</TableCell>
              <TableCell $center $highlight><CheckMark /></TableCell>
              <TableCell $center><CrossMark /></TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Smart Scheduling & Analytics</TableCell>
              <TableCell $center $highlight><CheckMark /></TableCell>
              <TableCell $center><CrossMark /></TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Assignment Reminders & Quick Actions</TableCell>
              <TableCell $center $highlight><CheckMark /></TableCell>
              <TableCell $center><CrossMark /></TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Study Tracking & Progress Insights</TableCell>
              <TableCell $center $highlight><CheckMark /></TableCell>
              <TableCell $center><CrossMark /></TableCell>
            </TableRow>
          </ComparisonTable>
        </ComparisonSection>

        <CTASection>
          <SectionTitle>Ready to Excel?</SectionTitle>
          <Text style={{ marginBottom: '2rem' }}>
            Join students who are already mastering their semesters.
          </Text>
          <CTAButton to="/register">Get Started for Free</CTAButton>
        </CTASection>
      </ContentSection>

      {isModalOpen && activeSlide && (
        <SlideModalOverlay role="dialog" aria-modal="true" aria-label={`${activeSlide.title} details`} onClick={closeModal}>
          <SlideModalCard onClick={(event) => event.stopPropagation()}>
            <SlideModalClose onClick={closeModal} aria-label="Close focused feature view">×</SlideModalClose>
            <ModalIcon>
              {React.createElement(activeSlide.icon)}
            </ModalIcon>
            <ModalTitle>{activeSlide.title}</ModalTitle>
            <ModalSummary>{activeSlide.summary}</ModalSummary>
            <ModalPoints>
              {activeSlide.points.map((point) => (
                <SlidePoint key={point}>{point}</SlidePoint>
              ))}
            </ModalPoints>
            <ModalDetails>{activeSlide.details}</ModalDetails>
          </SlideModalCard>
        </SlideModalOverlay>
      )}
    </PageContainer>
  );
};

export default About;
